import { PageMeta } from '@/components/seo/PageMeta'
import { LegalContactCard, LegalPage, type LegalSection } from '@/components/landing/misc/LegalPage'

const sections: LegalSection[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    body: (
      <>
        <p>
          Templatean Corporation ("Templatean," "we," "us," or "our") is committed to protecting your privacy.
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
          use our services, including our website, applications, and Office Add-ins.
        </p>
        <p>
          By using our services, you agree to the collection and use of information in accordance with this policy.
        </p>
      </>
    ),
  },
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    body: (
      <>
        <h3>Personal Information</h3>
        <p>We may collect personal information that you voluntarily provide to us when you:</p>
        <ul>
          <li>Register for an account</li>
          <li>Subscribe to our services</li>
          <li>Contact us for support</li>
          <li>Sign up for our newsletter</li>
          <li>Participate in surveys or promotions</li>
        </ul>
        <p>
          This information may include your name, email address, company name, job title, phone number,
          and billing information.
        </p>

        <h3>Usage Information</h3>
        <p>We automatically collect certain information when you use our services, including:</p>
        <ul>
          <li>Device information (type, operating system, browser)</li>
          <li>IP address and location data</li>
          <li>Usage patterns and feature interactions</li>
          <li>Error logs and performance data</li>
        </ul>

        <h3>Document Content</h3>
        <p>
          When you use our Office Add-ins or AI features, we may process document content to provide our services.
          This content is processed in accordance with your instructions and our data processing agreements.
        </p>
      </>
    ),
  },
  {
    id: 'how-we-use',
    title: 'How We Use Your Information',
    body: (
      <>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve our services</li>
          <li>Process transactions and send related information</li>
          <li>Send technical notices, updates, and support messages</li>
          <li>Respond to your comments, questions, and requests</li>
          <li>Monitor and analyze usage trends and preferences</li>
          <li>Detect, prevent, and address technical issues</li>
          <li>Protect against fraudulent or unauthorized activity</li>
          <li>Comply with legal obligations</li>
        </ul>
      </>
    ),
  },
  {
    id: 'sharing',
    title: 'Information Sharing and Disclosure',
    body: (
      <>
        <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
        <ul>
          <li><strong>Service Providers:</strong> With third-party vendors who assist in providing our services</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
          <li><strong>With Your Consent:</strong> When you have given us explicit permission</li>
        </ul>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Data Security',
    body: (
      <>
        <p>We implement appropriate technical and organizational measures to protect your information, including:</p>
        <ul>
          <li>Encryption of data in transit and at rest (AES-256)</li>
          <li>Regular security assessments and penetration testing</li>
          <li>Access controls and authentication requirements</li>
          <li>Employee training on data protection</li>
          <li>Incident response procedures</li>
        </ul>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'Data Retention',
    body: (
      <p>
        We retain your information for as long as necessary to provide our services and fulfill the purposes
        described in this policy. When you delete your account, we will delete or anonymize your personal
        information within 30 days, unless retention is required by law.
      </p>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    body: (
      <>
        <p>Depending on your location, you may have the following rights:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of your personal information</li>
          <li><strong>Correction:</strong> Request correction of inaccurate information</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information</li>
          <li><strong>Portability:</strong> Request transfer of your data to another service</li>
          <li><strong>Objection:</strong> Object to processing of your information</li>
          <li><strong>Restriction:</strong> Request restriction of processing</li>
        </ul>
        <p>
          To exercise these rights, please contact us at{' '}
          <a href="mailto:privacy@templatean.com">privacy@templatean.com</a>.
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies and Tracking',
    body: (
      <p>
        We use cookies and similar technologies to enhance your experience, analyze usage, and assist in
        our marketing efforts. You can control cookie preferences through your browser settings. Note that
        disabling cookies may affect the functionality of our services.
      </p>
    ),
  },
  {
    id: 'international-transfers',
    title: 'International Data Transfers',
    body: (
      <p>
        Your information may be transferred to and processed in countries other than your own. We ensure
        appropriate safeguards are in place, including Standard Contractual Clauses approved by relevant
        authorities, to protect your information during such transfers.
      </p>
    ),
  },
  {
    id: 'children',
    title: "Children's Privacy",
    body: (
      <p>
        Our services are not intended for individuals under 16 years of age. We do not knowingly collect
        personal information from children. If you become aware that a child has provided us with personal
        information, please contact us.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    body: (
      <p>
        We may update this Privacy Policy from time to time. We will notify you of any changes by posting
        the new policy on this page and updating the "Last updated" date. We encourage you to review this
        policy periodically.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact Us',
    body: (
      <>
        <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
        <LegalContactCard email="privacy@templatean.com" />
      </>
    ),
  },
]

export default function Privacy() {
  return (
    <>
      <PageMeta
        title="Privacy Policy | Templatean"
        description="Learn how Templatean collects, uses, and protects your personal information. Our commitment to your privacy and data security."
      />
      <LegalPage
        title="Privacy"
        highlight="policy."
        lede="How Templatean collects, uses, and protects your personal information: our commitment to your privacy and data security."
        lastUpdated="January 28, 2026"
        sections={sections}
        related={{ label: 'Terms of Service', href: '/terms' }}
      />
    </>
  )
}
